import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  FiCheckCircle,
  FiLayers,
  FiChevronDown,
  FiCheck,
  FiSearch,
  FiBookOpen,
  FiAward,
  FiGrid,
  FiUserCheck,
} from "react-icons/fi";
import { userRequest } from "../../../utils/requestMethods";
import { generateSchoolSessions } from "../../../utils/schoolYearUtils";
import { useHistory } from "react-router-dom";
import { useStateContext } from "../../../contexts/ContextProvider";
import {
  createSharedTest,
  extractApiErrorMessage,
  listClassGroups,
  getClassGroupId,
} from "../../../utils/classGroupApi";
import { getArmLabel, pluralize, splitArmName } from "../../../utils/classGroupHelpers";

// Modern custom select component replacing browser native <select>
const ModernSelect = ({
  label,
  required = false,
  value,
  onChange,
  options = [],
  placeholder = "Select an option",
  disabled = false,
  loading = false,
  helperText = "",
  searchable = false,
  icon: TriggerIcon,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const selectedOption = options.find((opt) => String(opt.value) === String(value));

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const term = searchTerm.toLowerCase();
    return options.filter(
      (opt) =>
        opt.label?.toLowerCase().includes(term) ||
        opt.subtitle?.toLowerCase().includes(term) ||
        opt.badge?.toLowerCase().includes(term),
    );
  }, [options, searchTerm]);

  return (
    <div className="relative" ref={containerRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-900 mb-1.5">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <button
        type="button"
        disabled={disabled || loading}
        onClick={() => {
          if (!disabled && !loading) setIsOpen((prev) => !prev);
        }}
        className={`w-full flex items-center justify-between px-3.5 py-2.5 bg-gray-50 border text-sm rounded-xl text-left transition-all ${
          isOpen
            ? "border-indigo-500 ring-2 ring-indigo-100 bg-white shadow-sm"
            : "border-gray-300 hover:border-gray-400"
        } ${disabled || loading ? "opacity-60 cursor-not-allowed bg-gray-100" : "cursor-pointer"}`}
      >
        <div className="flex items-center gap-2.5 truncate pr-2">
          {TriggerIcon && <TriggerIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />}
          {selectedOption?.icon && (
            <span className="flex-shrink-0 text-indigo-600">{selectedOption.icon}</span>
          )}
          <div className="truncate">
            {loading ? (
              <span className="text-gray-400">Loading options...</span>
            ) : selectedOption ? (
              <span className="text-gray-900 font-medium">{selectedOption.label}</span>
            ) : (
              <span className="text-gray-400">{placeholder}</span>
            )}
          </div>
          {selectedOption?.badge && (
            <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100 flex-shrink-0">
              {selectedOption.badge}
            </span>
          )}
        </div>
        <FiChevronDown
          className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${
            isOpen ? "rotate-180 text-indigo-600" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1.5 w-full bg-white rounded-xl shadow-xl border border-gray-200 py-1.5 max-h-60 overflow-y-auto focus:outline-none animate-fadeIn">
          {searchable && options.length > 5 && (
            <div className="px-2.5 pb-2 pt-1 border-b border-gray-100 sticky top-0 bg-white z-10">
              <div className="relative flex items-center">
                <FiSearch className="w-3.5 h-3.5 text-gray-400 absolute left-2.5" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Type to search..."
                  className="w-full pl-8 pr-2 py-1.5 text-xs bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:bg-white"
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
          )}

          {filteredOptions.length === 0 ? (
            <div className="px-4 py-3 text-xs text-gray-400 text-center">
              {options.length === 0 ? "No options available" : "No matches found"}
            </div>
          ) : (
            filteredOptions.map((opt) => {
              const isSelected = String(opt.value) === String(value);
              return (
                <button
                  key={String(opt.value)}
                  type="button"
                  onClick={() => {
                    onChange(opt.value, opt);
                    setIsOpen(false);
                    setSearchTerm("");
                  }}
                  className={`w-full text-left px-3.5 py-2.5 text-sm flex items-center justify-between transition-colors ${
                    isSelected
                      ? "bg-indigo-50/80 text-indigo-900 font-semibold"
                      : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                  }`}
                >
                  <div className="flex items-center gap-2.5 truncate pr-2">
                    {opt.icon && (
                      <span className="flex-shrink-0 text-gray-400 group-hover:text-indigo-600">
                        {opt.icon}
                      </span>
                    )}
                    <div className="truncate">
                      <div className="truncate">{opt.label}</div>
                      {opt.subtitle && (
                        <div className="text-xs text-gray-400 font-normal">{opt.subtitle}</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {opt.badge && (
                      <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold bg-gray-100 text-gray-600">
                        {opt.badge}
                      </span>
                    )}
                    {isSelected && <FiCheck className="w-4 h-4 text-indigo-600" />}
                  </div>
                </button>
              );
            })
          )}
        </div>
      )}
      {helperText && <p className="text-xs text-gray-500 mt-1">{helperText}</p>}
    </div>
  );
};

const AddTestModal = ({
  closeModal,
  school,
  subjects: propsSubjects,
  classes: propsClasses,
  onTestAdded,
  prefill = null, // { groupId, sharedSubjectId } — open on "all arms" with these selected
}) => {
  const { user } = useStateContext();
  const history = useHistory();
  const [subjects, setSubjects] = useState(propsSubjects || []);
  // Keep the teacher's assignment list separate from the currently displayed group catalog.
  // A group catalog contains every shared subject; it is not itself a permission grant.
  const assignedSubjectsRef = useRef(propsSubjects || []);
  // Same list as state, so class/group options can be derived from it: a teacher who only
  // teaches a subject (and isn't class teacher of those arms) gets no classes from
  // teacher-assigned-classes, and would otherwise never be offered the class group.
  const [assignedSubjects, setAssignedSubjects] = useState(propsSubjects || []);
  const [classes, setClasses] = useState(propsClasses || []);
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [classesLoading, setClassesLoading] = useState(false);

  // Determine if this is admin mode (admin role or classes prop provided)
  const isAdminMode = user?.role === 0 || (propsClasses && propsClasses.length > 0);

  // Target Scope: "group" (Class Group - All Arms) vs "single" (Single Arm / Class)
  const [targetScope, setTargetScope] = useState(
    school?.settings?.class_grouping_enabled ? "group" : "single",
  );

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    duration: 0,
    total_score: 0,
    school: school?._id || "",
    class: "",
    subject: "",
    is_active: true,
    term: school?.settings?.current_term || 1,
    session: school?.settings?.current_session || "",
    type: "",
    ca: 0,
    category: "assessment",
    scoring_mode: "full",
    mcq_max_score: 0,
    theory_max_score: 0,
    start_date: "",
    end_date: "",
  });
  const schoolSessions = useMemo(() => generateSchoolSessions(2022, 20), []);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const [sharedTestResult, setSharedTestResult] = useState(null);
  const [classGroups, setClassGroups] = useState([]);

  // Fetch class groups whenever class grouping is enabled
  useEffect(() => {
    if (!school?.settings?.class_grouping_enabled || !school?._id) {
      setTargetScope("single");
      return;
    }

    let cancelled = false;
    listClassGroups(school._id)
      .then((groups) => {
        if (!cancelled) {
          const list = Array.isArray(groups) ? groups : [];
          setClassGroups(list);
          if (list.length === 0) {
            setTargetScope("single");
          }
        }
      })
      .catch((err) => {
        console.error("Error fetching class groups:", err);
      });

    return () => {
      cancelled = true;
    };
  }, [school?.settings?.class_grouping_enabled, school?._id]);

  // Fetch classes if not provided as props
  useEffect(() => {
    const fetchClasses = async () => {
      if (propsClasses && propsClasses.length > 0) {
        setClasses(propsClasses);
        return;
      }

      if (!school?._id) return;

      setClassesLoading(true);
      try {
        if (isAdminMode || formData.category === "admission") {
          const { data } = await userRequest.get(`classes?code=${school.code}`);
          setClasses(data.classes || []);
        } else if (user?._id) {
          const { data } = await userRequest.get(
            `/teachers/teacher-assigned-classes/${school._id}/${user._id}?limit=50`,
          );
          setClasses(data.classes || []);
        }
      } catch (error) {
        console.error("Error fetching classes:", error);
        setClasses([]);
      } finally {
        setClassesLoading(false);
      }
    };

    fetchClasses();
  }, [school?.code, school?._id, user?._id, isAdminMode, propsClasses, formData.category]);

  // Fetch subjects based on mode
  useEffect(() => {
    // A slower earlier request (e.g. the teacher's subjects) must not overwrite the group's
    // subjects fetched after a class level was picked.
    let cancelled = false;
    const fetchSubjects = async () => {
      const isGroup = formData.class?.startsWith("group_");
      const classParam = isGroup ? formData.class.replace("group_", "") : formData.class;

      // When a class group is selected, fetch the canonical shared subjects of the group
      if (isGroup && school?._id) {
        setSubjectsLoading(true);
        try {
          // The group's subjects are filtered by the teacher's assignments, so make sure
          // those are loaded (a class level can be picked before they arrive).
          if (!isAdminMode && assignedSubjectsRef.current.length === 0 && user?._id) {
            const { data: assigned } = await userRequest.get(
              `/teachers/teacher-assigned-subjects/${school._id}/${user._id}?limit=100`,
            );
            assignedSubjectsRef.current = assigned.subjects || [];
            setAssignedSubjects(assignedSubjectsRef.current);
          }
          const { data } = await userRequest.get(
            `/class-groups/${school._id}/${classParam}/subjects`,
          );
          if (cancelled) return;
          const assignedSharedSubjectIds = new Set(
            (assignedSubjectsRef.current || [])
              .filter((subject) => {
                const assignedGroupId = getClassGroupId(
                  subject.class_group_id || subject.class?.class_group_id,
                );
                return assignedGroupId === String(classParam);
              })
              .map((subject) => subject.subject_ref_id || subject.shared_subject_id)
              .filter(Boolean)
              .map(String),
          );

          const groupSubjs = (data.subjects || data || [])
            .filter(
              (subject) =>
                isAdminMode || assignedSharedSubjectIds.has(String(subject._id)),
            )
            .map((s) => ({ ...s, is_group_subject: true }));
          setSubjects(groupSubjs);
        } catch (error) {
          console.error("Error fetching class group subjects:", error);
          if (!cancelled) setSubjects([]);
        } finally {
          setSubjectsLoading(false);
        }
        return;
      }

      // Admin mode: fetch single class subjects
      if (isAdminMode) {
        if (!formData.class || !school?._id) {
          setSubjects([]);
          return;
        }

        setSubjectsLoading(true);
        try {
          const { data } = await userRequest.get(`/subjects/${school._id}/${classParam}?limit=100`);
          if (cancelled) return;
          const classInfo = classes.find((c) => c._id === classParam);
          const subjectsWithClass = (data.subjects || []).map((subject) => ({
            ...subject,
            class: classInfo,
          }));
          setSubjects(subjectsWithClass);
        } catch (error) {
          console.error("Error fetching subjects:", error);
          if (!cancelled) setSubjects([]);
        } finally {
          setSubjectsLoading(false);
        }
        return;
      }

      // Teacher mode: fetch assigned subjects
      if (propsSubjects && propsSubjects.length > 0) {
        setSubjects(propsSubjects);
        assignedSubjectsRef.current = propsSubjects;
        setAssignedSubjects(propsSubjects);
        return;
      }

      if (!school?._id || !user?._id) return;

      setSubjectsLoading(true);
      try {
        const { data } = await userRequest.get(
          `/teachers/teacher-assigned-subjects/${school._id}/${user._id}?limit=100`,
        );
        const teacherSubjects = data.subjects || [];
        // The assignments are still valid if this run was superseded; only the display isn't.
        assignedSubjectsRef.current = teacherSubjects;
        setAssignedSubjects(teacherSubjects);
        if (cancelled) return;
        setSubjects(teacherSubjects);
      } catch (error) {
        console.error("Error fetching assigned subjects:", error);
        if (!cancelled) setSubjects([]);
      } finally {
        setSubjectsLoading(false);
      }
    };

    fetchSubjects();
    return () => {
      cancelled = true;
    };
  }, [
    school?._id,
    user?._id,
    propsSubjects,
    isAdminMode,
    formData.class,
    classes,
  ]);

  const armLabel = getArmLabel(school);
  const armLower = armLabel.toLowerCase();
  const armsLower = pluralize(armLabel, 2).toLowerCase();

  // Every class the teacher works with: classes they're class teacher of, plus (for a
  // teacher) the classes of the subjects they teach. Deduplicated by id.
  const teacherClassList = useMemo(() => {
    const byId = new Map();
    classes.forEach((cls) => {
      const classData = cls.class || cls;
      if (classData?._id) byId.set(String(classData._id), classData);
    });
    if (!isAdminMode) {
      assignedSubjects.forEach((subject) => {
        const classData = subject.class;
        if (classData?._id && !byId.has(String(classData._id))) {
          byId.set(String(classData._id), classData);
        }
      });
    }
    return Array.from(byId.values());
  }, [classes, assignedSubjects, isAdminMode]);

  // Grouped Class Options (Entire Class Group)
  const classGroupOptions = useMemo(() => {
    const groupsMap = new Map();

    teacherClassList.forEach((classData) => {
      const groupId = getClassGroupId(classData.class_group_id);

      if (groupId) {
        if (!groupsMap.has(groupId)) {
          const groupDoc = (classGroups || []).find((g) => String(g._id) === String(groupId));
          const grpName =
            groupDoc?.name || splitArmName(classData.class_name)?.base || "Class Group";
          groupsMap.set(groupId, {
            value: `group_${groupId}`,
            label: grpName,
            groupId,
            groupName: grpName,
            badge: "All arms",
            arms: [],
            levelOfEducation: classData.level_of_education,
            icon: <FiLayers className="w-4 h-4 text-indigo-600" />,
          });
        }
        groupsMap.get(groupId).arms.push(classData);
      }
    });

    return Array.from(groupsMap.values()).map((g) => ({
      ...g,
      // Only the arms this teacher works with — the test itself reaches every arm that
      // takes the subject, which the notice under the selects spells out.
      subtitle: `Yours: ${g.arms.map((a) => a.class_name).join(", ")}`,
    }));
  }, [teacherClassList, classGroups]);

  // Individual Class / Arm Options
  const individualClassOptions = useMemo(() => {
    return teacherClassList.map((classData) => {
      const groupId = getClassGroupId(classData.class_group_id);
      const groupDoc = groupId
        ? (classGroups || []).find((g) => String(g._id) === String(groupId))
        : null;

      return {
        value: classData._id,
        label: classData.class_name,
        subtitle: groupDoc ? `Member of ${groupDoc.name}` : classData.level_of_education || "",
        badge: groupDoc ? armLabel : "",
        classData,
        icon: <FiGrid className="w-4 h-4 text-gray-400" />,
      };
    });
  }, [teacherClassList, classGroups, armLabel]);

  // A subject-only teacher's classes come from their subjects, so wait for those too.
  const classListLoading =
    classesLoading || (!isAdminMode && subjectsLoading && teacherClassList.length === 0);

  const isClassGroupingEnabled = Boolean(
    school?.settings?.class_grouping_enabled && classGroupOptions.length > 0,
  );

  // Apply the prefill once the teacher's subjects (and so the group option) are available.
  // Waiting for them matters: the group's subject list is filtered by those assignments.
  const prefillApplied = useRef(false);
  useEffect(() => {
    if (!prefill?.groupId || prefillApplied.current || assignedSubjects.length === 0) return;
    const groupValue = `group_${prefill.groupId}`;
    if (!classGroupOptions.some((opt) => opt.value === groupValue)) return;
    prefillApplied.current = true;
    setTargetScope("group");
    setFormData((prev) => ({
      ...prev,
      class: groupValue,
      subject: prefill.sharedSubjectId ? String(prefill.sharedSubjectId) : "",
      ca: 0,
    }));
  }, [prefill, assignedSubjects, classGroupOptions]);

  // Active Class Options based on Scope Switcher
  const activeClassOptions =
    isClassGroupingEnabled && targetScope === "group" ? classGroupOptions : individualClassOptions;

  const isClassGroupSelected = Boolean(
    isClassGroupingEnabled && (targetScope === "group" || formData.class?.startsWith("group_")),
  );
  const selectedTargetGroupId =
    isClassGroupingEnabled && formData.class?.startsWith("group_")
      ? formData.class.replace("group_", "")
      : null;

  const selectedSubject = useMemo(() => {
    return subjects.find((subj) => String(subj._id) === String(formData.subject));
  }, [subjects, formData.subject]);

  const selectedClassOption = useMemo(() => {
    return activeClassOptions.find((opt) => String(opt.value) === String(formData.class));
  }, [activeClassOptions, formData.class]);

  const selectedClass = useMemo(() => {
    if (isClassGroupSelected) {
      return selectedClassOption?.arms?.[0] || selectedSubject?.class;
    }
    return (
      teacherClassList.find((c) => String(c._id) === String(formData.class)) ||
      selectedSubject?.class
    );
  }, [teacherClassList, formData.class, isClassGroupSelected, selectedClassOption, selectedSubject]);

  const classGroupId =
    selectedTargetGroupId ||
    (isClassGroupingEnabled
      ? getClassGroupId(selectedClass?.class_group_id || selectedSubject?.class?.class_group_id)
      : null);

  const selectedClassGroup = classGroups.find((g) => g._id === classGroupId);
  const groupDisplayName =
    selectedClassGroup?.name || selectedClassOption?.groupName || "Class Group";

  // Compute available subjects based on Scope & Selected Class
  const availableSubjectOptions = useMemo(() => {
    if (isClassGroupingEnabled && targetScope === "group" && isClassGroupSelected) {
      const groupSubjs = subjects.filter((s) => {
        const gId = getClassGroupId(s.class?.class_group_id);
        return gId === selectedTargetGroupId || s.is_group_subject;
      });

      const seen = new Set();
      const deduplicated = [];

      groupSubjs.forEach((s) => {
        const key = s.subject_ref_id
          ? String(s.subject_ref_id)
          : s.subject_name?.trim().toLowerCase();
        if (key && !seen.has(key)) {
          seen.add(key);
          deduplicated.push({
            value: s._id,
            label: s.subject_name,
            badge: "Shared",
            raw: s,
            icon: <FiBookOpen className="w-4 h-4 text-indigo-500" />,
          });
        }
      });

      return deduplicated;
    }

    if (formData.class) {
      return subjects
        .filter((s) => (s.class?._id || s.class) === formData.class)
        .map((s) => ({
          value: s._id,
          label: s.subject_name,
          badge: isClassGroupingEnabled && s.subject_ref_id ? "Shared" : "",
          raw: s,
          icon: <FiBookOpen className="w-4 h-4 text-gray-400" />,
        }));
    }

    // Default when no class is selected yet
    const seen = new Set();
    const deduplicated = [];

    subjects.forEach((s) => {
      const gId = getClassGroupId(s.class?.class_group_id);
      const isShared = Boolean(s.subject_ref_id || s.is_group_subject);

      if (isClassGroupingEnabled && targetScope === "group") {
        if (isShared && gId) {
          const key = `${gId}_${s.subject_ref_id || s.subject_name?.trim().toLowerCase()}`;
          if (!seen.has(key)) {
            seen.add(key);
            deduplicated.push({
              value: s._id,
              label: s.subject_name,
              subtitle: groupDisplayName,
              badge: "Shared",
              raw: s,
              icon: <FiBookOpen className="w-4 h-4 text-indigo-500" />,
            });
          }
        }
      } else {
        deduplicated.push({
          value: s._id,
          label: s.subject_name,
          subtitle: s.class?.class_name || "",
          raw: s,
          icon: <FiBookOpen className="w-4 h-4 text-gray-400" />,
        });
      }
    });

    return deduplicated;
  }, [
    formData.class,
    isClassGroupSelected,
    isClassGroupingEnabled,
    selectedTargetGroupId,
    subjects,
    groupDisplayName,
    targetScope,
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const numericFields = [
      "duration",
      "total_score",
      "ca",
      "term",
      "mcq_max_score",
      "theory_max_score",
    ];
    const isNumeric = numericFields.includes(name);

    setFormData((prev) => {
      const updated = {
        ...prev,
        [name]: isNumeric ? Number(value) : value,
      };

      if (name === "scoring_mode" && value === "full") {
        updated.mcq_max_score = 0;
        updated.theory_max_score = 0;
      }

      if (name === "mcq_max_score") {
        updated.theory_max_score = Math.max(0, updated.total_score - Number(value));
      }

      if (name === "total_score") {
        if (updated.scoring_mode === "partial_mcq" && updated.mcq_max_score > 0) {
          updated.theory_max_score = Math.max(0, Number(value) - updated.mcq_max_score);
        }
      }

      if (name === "type" && value !== "exam") {
        updated.scoring_mode = "full";
        updated.mcq_max_score = 0;
        updated.theory_max_score = 0;
      }

      return updated;
    });
  };

  const handleScopeChange = (newScope) => {
    if (newScope === targetScope) return;
    setTargetScope(newScope);
    setFormData((prev) => ({
      ...prev,
      class: "",
      subject: "",
      ca: 0,
    }));
  };

  const handleClassSelect = (classVal) => {
    setFormData((prevData) => {
      const selectedOpt = activeClassOptions.find((opt) => String(opt.value) === String(classVal));
      const targetClass =
        selectedOpt?.classData ||
        selectedOpt?.arms?.[0] ||
        teacherClassList.find((c) => String(c._id || c.class?._id) === String(classVal));

      let nextTotalScore = prevData.total_score;
      if (prevData.category === "assessment" && prevData.type === "exam") {
        const assessmentSystem = getAssessmentSystemForSubject({ class: targetClass });
        if (assessmentSystem?.exam?.max_score) {
          nextTotalScore = assessmentSystem.exam.max_score;
        }
      }

      return {
        ...prevData,
        class: classVal,
        subject: "",
        ca: 0,
        total_score: nextTotalScore || prevData.total_score,
      };
    });
  };

  const handleSubjectSelect = (subjectVal, optionObj) => {
    const selectedSubj = optionObj?.raw || subjects.find((s) => s._id === subjectVal);
    const isShared = Boolean(selectedSubj?.subject_ref_id || selectedSubj?.is_group_subject);
    const subjClass = selectedSubj?.class || teacherClassList.find((c) => c._id === formData.class);
    const grpId = getClassGroupId(subjClass?.class_group_id);

    let nextClass = formData.class;
    if (!formData.class) {
      if (isClassGroupingEnabled && targetScope === "group" && isShared && grpId) {
        nextClass = `group_${grpId}`;
      } else if (selectedSubj?.class?._id) {
        nextClass = selectedSubj.class._id;
      }
    }

    const caOptions = getCAOptionsForSubject(selectedSubj);
    const currentCa = formData.ca;
    const isCaValid = currentCa > 0 && currentCa <= caOptions.length;
    const shouldAutoSelectCA =
      formData.type === "ca" && (isCaValid ? currentCa : caOptions.length === 1 ? 1 : 0);

    const updates = {
      subject: subjectVal,
      class: nextClass,
      ca: formData.type === "ca" ? shouldAutoSelectCA : 0,
    };

    if (formData.category === "assessment") {
      const assessmentSystem = getAssessmentSystemForSubject(selectedSubj);
      if (formData.type === "exam") {
        updates.total_score = assessmentSystem?.exam?.max_score || formData.total_score || 100;
      } else if (formData.type === "ca") {
        const caIdx = shouldAutoSelectCA > 0 ? shouldAutoSelectCA - 1 : null;
        if (caIdx !== null && caOptions[caIdx]) {
          updates.total_score = caOptions[caIdx].max_score;
        }
      }
    }

    setFormData((prevData) => ({ ...prevData, ...updates }));
  };

  // Get assessment system based on subject's class level of education or school fallback
  const getAssessmentSystemForSubject = (subject) => {
    const levelOfEducation =
      subject?.class?.level_of_education ||
      selectedClass?.level_of_education ||
      selectedClassOption?.levelOfEducation ||
      teacherClassList.find((c) => c._id === formData.class || c.class?._id === formData.class)
        ?.level_of_education ||
      teacherClassList.find((c) => c._id === formData.class || c.class?._id === formData.class)?.class
        ?.level_of_education;

    const assessmentSettings = school?.settings?.assessment;
    const systems = assessmentSettings?.assessment_system;

    if (Array.isArray(systems) && systems.length > 0) {
      if (levelOfEducation) {
        const matched = systems.find(
          (s) =>
            String(s.level_of_education).toLowerCase() === String(levelOfEducation).toLowerCase(),
        );
        if (matched) return matched;
      }
      const defaultSystem = systems.find((s) => s.is_default);
      if (defaultSystem) return defaultSystem;
      return systems[0];
    }

    // Direct assessment settings fallback (if single config or flat structure)
    if (assessmentSettings?.ca?.cas || assessmentSettings?.exam) {
      return assessmentSettings;
    }

    return { ca: { cas: [] }, exam: { max_score: 100 } };
  };

  const getCAOptionsForSubject = (subject) => {
    const assessmentSystem = getAssessmentSystemForSubject(subject);
    const cas = assessmentSystem?.ca?.cas;
    if (Array.isArray(cas) && cas.length > 0) {
      return cas.map((ca, idx) => ({
        name: ca.title || ca.name || `CA ${idx + 1}`,
        max_score: ca.max_score ?? ca.score ?? 20,
      }));
    }
    return [];
  };

  const getCurrentCAOptions = () => {
    const activeSubj =
      selectedSubject || availableSubjectOptions.find((s) => s.value === formData.subject)?.raw;
    return getCAOptionsForSubject(activeSubj);
  };

  const categoryOptions = [
    { value: "assessment", label: "Assessment", subtitle: "Official CA & Examinations" },
    { value: "learning", label: "Learning", subtitle: "Practice Tests & Homework" },
    { value: "admission", label: "Admission", subtitle: "Entrance & Mock Examinations" },
  ];

  const typeOptions = useMemo(() => {
    if (formData.category === "assessment") {
      return [
        { value: "ca", label: "Continuous Assessment (CA)", badge: "Graded" },
        { value: "exam", label: "Examination", badge: "Term Exam" },
      ];
    }
    if (formData.category === "admission") {
      return [
        { value: "exam", label: "Exam" },
        { value: "mock", label: "Mock Exam" },
        { value: "practice", label: "Practice Test" },
      ];
    }
    return [
      { value: "practice", label: "Practice Test" },
      { value: "mock", label: "Mock Exam" },
      { value: "homework", label: "Homework" },
    ];
  }, [formData.category]);

  const termOptions = [
    { value: 1, label: "First Term" },
    { value: 2, label: "Second Term" },
    { value: 3, label: "Third Term" },
  ];

  const sessionOptions = useMemo(() => {
    return (schoolSessions || []).map((s) => ({
      value: typeof s === "object" ? s.value : s,
      label: typeof s === "object" ? s.label : s,
    }));
  }, [schoolSessions]);

  const caSelectOptions = useMemo(() => {
    return getCurrentCAOptions().map((caItem, index) => ({
      value: index + 1,
      label: caItem.name || `CA ${index + 1}`,
      badge: `Max: ${caItem.max_score} marks`,
      caItem,
    }));
  }, [
    formData.subject,
    selectedSubject,
    availableSubjectOptions,
    selectedClass,
    selectedClassOption,
    school?.settings?.assessment,
  ]);

  const scoringModeOptions = [
    {
      value: "full",
      label: "Full Automatic Scoring (Objective Only)",
      subtitle: "System automatically grades all multiple choice questions",
    },
    {
      value: "partial_mcq",
      label: "Partial Auto-Scoring (Theory + MCQ)",
      subtitle: "MCQ portion auto-graded; Theory manually marked later",
    },
  ];

  const submitHandler = async (e) => {
    e.preventDefault();

    if (!formData.class) {
      setError(
        isClassGroupingEnabled && targetScope === "group"
          ? "Please select a class level."
          : "Please select a class.",
      );
      return;
    }

    if (formData.category !== "admission" && !formData.subject) {
      setError("Please select a subject.");
      return;
    }

    if (!formData.type) {
      setError("Please select a test type.");
      return;
    }

    if (formData.type === "ca" && (!formData.ca || formData.ca === 0)) {
      setError("Please select which CA this test is for.");
      return;
    }

    if (formData.type === "exam" && formData.scoring_mode === "partial_mcq") {
      if (!formData.mcq_max_score || formData.mcq_max_score <= 0) {
        setError("Please enter the MCQ max score for partial scoring.");
        return;
      }
      if (formData.mcq_max_score >= formData.total_score) {
        setError(`MCQ score must be less than the total score (${formData.total_score}).`);
        return;
      }
    }

    setLoading(true);
    try {
      const payload = { ...formData };
      if (payload.category === "admission") {
        if (!payload.subject) {
          delete payload.subject;
        }
        delete payload.ca;
      }
      if (payload.category === "learning") {
        delete payload.ca;
      }
      if (payload.type === "exam") {
        delete payload.ca;
      }
      if (payload.start_date) {
        payload.start_date = new Date(payload.start_date).toISOString();
      } else {
        delete payload.start_date;
      }
      if (payload.end_date) {
        payload.end_date = new Date(payload.end_date).toISOString();
      } else {
        delete payload.end_date;
      }

      if (
        isClassGroupingEnabled &&
        targetScope === "group" &&
        isClassGroupSelected &&
        classGroupId
      ) {
        const sharedSubjectId =
          selectedSubject?.subject_ref_id || formData.subject || selectedSubject?._id;
        const sharedPayload = { ...payload, subject_id: sharedSubjectId };
        delete sharedPayload.school;
        delete sharedPayload.class;
        delete sharedPayload.subject;

        const res = await createSharedTest(school._id, classGroupId, sharedPayload);
        setSharedTestResult(res);
        if (onTestAdded) onTestAdded();
        return;
      }

      // Single class / arm test
      if (payload.class.startsWith("group_")) {
        payload.class = selectedClass?._id || teacherClassList[0]?._id;
      }

      const { data } = await userRequest.post("/test", payload);
      setSuccess(true);
      if (onTestAdded) {
        onTestAdded();
      }
      setTimeout(() => {
        setSuccess(false);
        closeModal();
        if (!isAdminMode) {
          history.push(`/t/tests/questions`, { test: data });
        }
      }, 2000);
    } catch (error) {
      console.error(error);
      setError(
        isClassGroupingEnabled && targetScope === "group"
          ? extractApiErrorMessage(error, "Failed to create shared test")
          : "An error occurred while creating the test.",
      );
      setTimeout(() => setError(""), 5000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-md flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Create Online Test</h2>
            <p className="text-gray-500 text-xs mt-0.5">
              Set up a new assessment for your students
            </p>
          </div>
          <button
            type="button"
            onClick={closeModal}
            className="text-gray-400 bg-transparent hover:bg-gray-100 hover:text-gray-900 rounded-xl text-sm w-8 h-8 flex items-center justify-center transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {sharedTestResult ? (
            <div className="space-y-4 py-2">
              <div className="flex items-center gap-3.5">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <FiCheckCircle className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-slate-900">
                    Test created for all {armsLower}
                  </h3>
                  <p className="text-xs text-slate-500">
                    Class level: <strong>{groupDisplayName}</strong> &middot; Subject:{" "}
                    <strong>
                      {sharedTestResult.shared_subject?.subject_name ||
                        selectedSubject?.subject_name}
                    </strong>
                  </p>
                </div>
              </div>

              <div className="p-4 rounded-xl border border-emerald-200 bg-emerald-50/70 space-y-1">
                <p className="text-sm font-semibold text-emerald-900">
                  "{sharedTestResult.test?.title}"
                </p>
                <p className="text-xs text-emerald-800 leading-relaxed">
                  Students in every {armLower} of <strong>{groupDisplayName}</strong> that
                  takes this subject will see it. There's no need to create a separate test
                  for each {armLower}.
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => {
                    closeModal();
                    if (sharedTestResult.test?._id) {
                      history.push(`/t/tests/questions`, { test: sharedTestResult.test });
                    }
                  }}
                  className="px-4 py-2 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl transition-colors shadow-sm"
                >
                  Add Questions Now →
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Status Messages */}
              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <div className="flex items-center">
                    <svg
                      className="w-5 h-5 text-red-500 mr-3 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span className="text-red-700 text-sm font-medium">{error}</span>
                  </div>
                </div>
              )}

              {success && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl">
                  <div className="flex items-center">
                    <svg
                      className="w-5 h-5 text-green-500 mr-3 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span className="text-green-700 text-sm font-medium">
                      Test created successfully!
                    </span>
                  </div>
                </div>
              )}

              <form onSubmit={submitHandler} className="space-y-4">
                {/* Category and Test Type */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ModernSelect
                    label="Category"
                    required
                    value={formData.category}
                    options={categoryOptions}
                    onChange={(val) => {
                      setFormData((prev) => {
                        const validTypes =
                          val === "assessment"
                            ? ["ca", "exam"]
                            : val === "admission"
                              ? ["exam", "mock", "practice"]
                              : ["practice", "mock", "homework"];
                        const nextType = validTypes.includes(prev.type) ? prev.type : "";
                        return {
                          ...prev,
                          category: val,
                          type: nextType,
                          ca: nextType === "ca" ? prev.ca : 0,
                        };
                      });
                    }}
                  />

                  <ModernSelect
                    label="Test Type"
                    required
                    value={formData.type}
                    options={typeOptions}
                    placeholder="Select test type"
                    onChange={(newType) => {
                      const selectedSubj =
                        availableSubjectOptions.find((subj) => subj.value === formData.subject)
                          ?.raw || selectedSubject;
                      const assessmentSystem = getAssessmentSystemForSubject(selectedSubj);
                      const caOptions = getCAOptionsForSubject(selectedSubj);

                      if (newType === "exam" && formData.category === "assessment") {
                        setFormData((prev) => ({
                          ...prev,
                          type: newType,
                          ca: 0,
                          total_score: assessmentSystem?.exam?.max_score || prev.total_score || 100,
                          scoring_mode: "full",
                          mcq_max_score: 0,
                          theory_max_score: 0,
                        }));
                        return;
                      }

                      if (newType === "ca" && formData.category === "assessment") {
                        const currentCa = formData.ca;
                        const isCaValid = currentCa > 0 && currentCa <= caOptions.length;
                        const defaultCa = isCaValid ? currentCa : caOptions.length === 1 ? 1 : 0;
                        const defaultScore =
                          defaultCa > 0 && caOptions[defaultCa - 1]
                            ? caOptions[defaultCa - 1].max_score
                            : formData.total_score;

                        setFormData((prev) => ({
                          ...prev,
                          type: newType,
                          ca: defaultCa,
                          total_score: defaultScore || prev.total_score,
                          scoring_mode: "full",
                          mcq_max_score: 0,
                          theory_max_score: 0,
                        }));
                        return;
                      }

                      setFormData((prev) => ({
                        ...prev,
                        type: newType,
                        ca: 0,
                        scoring_mode: newType !== "exam" ? "full" : prev.scoring_mode,
                        mcq_max_score: newType !== "exam" ? 0 : prev.mcq_max_score,
                        theory_max_score: newType !== "exam" ? 0 : prev.theory_max_score,
                      }));
                    }}
                  />
                </div>

                {/* Target Scope Switcher (every arm of a class level vs one class) */}
                {isClassGroupingEnabled && (
                  <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
                    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider">
                      Who should take this test?
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => handleScopeChange("group")}
                        className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                          targetScope === "group"
                            ? "bg-indigo-600 text-white shadow-sm ring-1 ring-indigo-600"
                            : "bg-white text-slate-700 border border-slate-200 hover:bg-slate-100"
                        }`}
                      >
                        <FiLayers className="w-3.5 h-3.5" />
                        <span>All {armsLower} of a class level</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => handleScopeChange("single")}
                        className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                          targetScope === "single"
                            ? "bg-indigo-600 text-white shadow-sm ring-1 ring-indigo-600"
                            : "bg-white text-slate-700 border border-slate-200 hover:bg-slate-100"
                        }`}
                      >
                        <FiGrid className="w-3.5 h-3.5" />
                        <span>Just one class</span>
                      </button>
                    </div>
                  </div>
                )}

                {/* Class and Subject Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ModernSelect
                    label={
                      isClassGroupingEnabled && targetScope === "group" ? "Class level" : "Class"
                    }
                    required
                    searchable
                    value={formData.class}
                    options={activeClassOptions}
                    loading={classListLoading}
                    placeholder={
                      classListLoading
                        ? "Loading classes..."
                        : isClassGroupingEnabled && targetScope === "group"
                          ? "Select a class level"
                          : "Select a class"
                    }
                    onChange={handleClassSelect}
                  />

                  <ModernSelect
                    label="Subject"
                    required
                    searchable
                    value={formData.subject}
                    options={availableSubjectOptions}
                    loading={subjectsLoading}
                    disabled={!formData.class && availableSubjectOptions.length === 0}
                    placeholder={
                      !formData.class
                        ? "Select a class first"
                        : subjectsLoading
                          ? "Loading subjects..."
                          : availableSubjectOptions.length === 0
                            ? "No subjects found"
                            : "Select a subject"
                    }
                    onChange={handleSubjectSelect}
                  />
                </div>

                {/* Class Group Notice Banner */}
                {targetScope === "group" && isClassGroupSelected && formData.class && (
                  <div className="p-3.5 rounded-xl border border-indigo-200 bg-indigo-50/70 text-xs text-indigo-900 flex items-center gap-2.5">
                    <FiLayers className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                    <span>
                      One test for <strong>{groupDisplayName}</strong>. Students in every{" "}
                      {armLower} that takes this subject will see it.
                    </span>
                  </div>
                )}

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-1.5">
                    Test Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="e.g., Mid-Term Assessment"
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 block w-full p-2.5 transition-all"
                    required
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-1.5">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Provide instructions or description for the test..."
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 block w-full p-2.5 transition-all"
                    required
                  />
                </div>

                {/* Duration and Total Score */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-1.5">
                      Duration (minutes) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="duration"
                      value={formData.duration}
                      onChange={handleChange}
                      min="1"
                      placeholder="60"
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 block w-full p-2.5 transition-all"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-1.5">
                      Total Score <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="total_score"
                      value={formData.total_score}
                      onChange={handleChange}
                      min="1"
                      placeholder="100"
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 block w-full p-2.5 transition-all"
                      required
                    />
                  </div>
                </div>

                {/* CA Selection (if applicable) */}
                {formData.category === "assessment" && formData.type === "ca" && (
                  <div>
                    <ModernSelect
                      label="Continuous Assessment (CA)"
                      required
                      value={formData.ca}
                      options={caSelectOptions}
                      placeholder="Select CA"
                      icon={FiAward}
                      onChange={(val, opt) => {
                        setFormData((prev) => ({
                          ...prev,
                          ca: Number(val),
                          total_score: opt?.caItem?.max_score || prev.total_score,
                        }));
                      }}
                      helperText={
                        formData.subject && caSelectOptions.length === 0
                          ? "No CA configuration found for this class level. Please check school assessment settings."
                          : ""
                      }
                    />
                  </div>
                )}

                {/* Scoring Mode (Exams only) */}
                {formData.type === "exam" && (
                  <div>
                    <ModernSelect
                      label="Scoring Mode"
                      value={formData.scoring_mode}
                      options={scoringModeOptions}
                      onChange={(val) => {
                        handleChange({ target: { name: "scoring_mode", value: val } });
                      }}
                    />
                  </div>
                )}

                {/* Partial MCQ scoring split */}
                {formData.type === "exam" && formData.scoring_mode === "partial_mcq" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-slate-50 border border-slate-200 rounded-xl">
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-1.5">
                        MCQ Max Score <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        name="mcq_max_score"
                        value={formData.mcq_max_score}
                        onChange={handleChange}
                        min="1"
                        max={formData.total_score - 1}
                        placeholder="e.g. 40"
                        className="bg-white border border-gray-300 text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 block w-full p-2.5 transition-all"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-1.5">
                        Theory Max Score (Auto-calculated)
                      </label>
                      <input
                        type="number"
                        value={formData.theory_max_score}
                        disabled
                        className="bg-gray-100 border border-gray-300 text-gray-600 text-sm rounded-xl block w-full p-2.5 cursor-not-allowed"
                      />
                    </div>
                  </div>
                )}

                {/* Term and Session */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ModernSelect
                    label="Term"
                    value={formData.term}
                    options={termOptions}
                    onChange={(val) => {
                      handleChange({ target: { name: "term", value: val } });
                    }}
                  />

                  <ModernSelect
                    label="Academic Session"
                    searchable
                    value={formData.session}
                    options={sessionOptions}
                    placeholder="Select Session"
                    onChange={(val) => {
                      handleChange({ target: { name: "session", value: val } });
                    }}
                  />
                </div>

                {/* Start Date and End Date */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-1.5">
                      Start Date & Time
                    </label>
                    <input
                      type="datetime-local"
                      name="start_date"
                      value={formData.start_date}
                      onChange={handleChange}
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 block w-full p-2.5 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-1.5">
                      End Date & Time
                    </label>
                    <input
                      type="datetime-local"
                      name="end_date"
                      value={formData.end_date}
                      onChange={handleChange}
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 block w-full p-2.5 transition-all"
                    />
                  </div>
                </div>

                {/* Submit Buttons */}
                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-5 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition-colors shadow-sm"
                  >
                    {loading ? "Creating..." : "Create Test"}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddTestModal;
